import { data, Shape } from 'azure-maps-control';
import * as atlas from 'azure-maps-control';

import { ExternalMapPoint } from '../WorldMap.types';
import { InternalMapPoint } from './useWorldMap.types';

export const mapExternalToInternalMapPoints = (
  points: ExternalMapPoint[]
): InternalMapPoint[] => {
  return points.map((point, index) => ({
    ...point,
    properties: {
      ...point.properties,
      index,
      isActive: point.properties?.isActive ?? false
    }
  }));
};

export const mapPointsToFeatures = (
  points: InternalMapPoint[]
): data.Feature<data.Point, InternalMapPoint['properties']>[] => {
  return points.map((point) => {
    return new data.Feature(new data.Point(point.coordinates), {
      ...point.properties
    });
  });
};

export const averagePosition = (points: InternalMapPoint[]): data.Position => {
  if (points.length === 0) {
    return [0, 0];
  }

  const sum = points.reduce(
    (acc, point) => {
      return [acc[0] + point.coordinates[0], acc[1] + point.coordinates[1]];
    },
    [0, 0]
  );

  return [sum[0] / points.length, sum[1] / points.length];
};

export const getZoomLevel = (points: InternalMapPoint[]): number => {
  if (points.length === 0) {
    return 2;
  }

  if (points.length === 1) {
    return 8;
  }

  const lats = points.map((p) => p.coordinates[1]);
  const lngs = points.map((p) => p.coordinates[0]);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);

  if (maxDiff > 50) return 2;
  if (maxDiff > 20) return 3;
  if (maxDiff > 10) return 4;
  if (maxDiff > 5) return 5;
  if (maxDiff > 2) return 6;
  if (maxDiff > 1) return 7;
  
  return 8;
};

export const getShapeAttributes = (shape: Shape | string) => {
  
  interface RawFeature { properties: Record<string, unknown>; geometry: { coordinates: data.Position } }

  const properties = (shape as Shape).getProperties?.() 
    ?? (shape as unknown as RawFeature).properties 
    ?? {};

  const coordinates = ((shape as Shape).getCoordinates?.() 
    ?? (shape as unknown as RawFeature).geometry?.coordinates) as data.Position;

  const point = properties.cluster
    ? undefined
    : {
        coordinates,
        customProperties: properties,
        source: properties.source
      };

  const cluster = properties.cluster
    ? {
        coordinates,
        properties,
        source: properties.source
      }
    : undefined;

  return { cluster, point };
};

export const setupMapLanguage = (languageCode: string) => {
  atlas.setLanguage(languageCode);
};
